'use client'

import React, { useEffect, useState } from 'react'

export function ScrollToTop() {
  const [isVisible, setIsVisible] = useState(false)
  const [scrollProgress, setScrollProgress] = useState(0)

  useEffect(() => {
    const handleScroll = () => {
      // Toggle visibility
      if (window.scrollY > 300) {
        setIsVisible(true)
      } else {
        setIsVisible(false)
      }

      // Compute scroll progress
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight
      if (totalHeight > 0) {
        const progress = (window.scrollY / totalHeight) * 100
        setScrollProgress(progress)
      } else {
        setScrollProgress(0)
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    // Run once initially to capture page height / position
    handleScroll()

    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }

  // Circular progress calculations
  const radius = 20
  const circumference = 2 * Math.PI * radius
  const strokeDashoffset = circumference - (scrollProgress / 100) * circumference

  return (
    <button
      onClick={scrollToTop}
      aria-label="Scroll to top"
      className={`fixed z-130 w-12 h-12 rounded-full bg-slate-950/60 dark:bg-slate-900/50 backdrop-blur-xl border border-white/10 hover:border-primary/40 shadow-2xl cursor-pointer flex items-center justify-center transition-all duration-500 ease-out group ${
        isVisible
          ? 'opacity-100 scale-100 pointer-events-auto'
          : 'opacity-0 scale-75 pointer-events-none'
      } bottom-20 right-4 sm:bottom-8 sm:right-8 hover:scale-105 active:scale-95`}
    >
      {/* SVG Scroll Progress Circle */}
      <svg className="absolute inset-0 w-full h-full -rotate-90 select-none pointer-events-none" viewBox="0 0 48 48">
        {/* Track circle */}
        <circle
          cx="24"
          cy="24"
          r={radius}
          fill="none"
          stroke="rgba(255, 255, 255, 0.08)"
          strokeWidth="2.5"
        />
        {/* Progress circle */}
        <circle
          cx="24"
          cy="24"
          r={radius}
          fill="none"
          stroke="var(--primary)"
          strokeWidth="2.5"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          className="transition-[stroke-dashoffset] duration-100 ease-out"
        />
      </svg>

      {/* Ladakh Mountain Ridges Staggered Chevron Icon */}
      <div className="relative w-5 h-5 flex flex-col items-center justify-center text-white/80 group-hover:text-primary transition-colors duration-300">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="w-full h-full"
        >
          {/* Top Primary Ridge */}
          <path
            d="M5 10L12 3L19 10"
            stroke="currentColor"
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            className="transform transition-transform duration-300 ease-out group-hover:-translate-y-1"
          />
          {/* Middle Semi-transparent Ridge */}
          <path
            d="M5 16L12 9L19 16"
            stroke="currentColor"
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            className="opacity-60 transform transition-transform duration-300 ease-out delay-75 group-hover:-translate-y-0.5"
          />
          {/* Bottom Ridge */}
          <path
            d="M8 21L12 17L16 21"
            stroke="currentColor"
            strokeWidth={1}
            strokeLinecap="round"
            strokeLinejoin="round"
            className="opacity-30 transform transition-transform duration-300 ease-out delay-150"
          />
        </svg>
      </div>

      {/* Slide-out Tooltip */}
      <span className="font-oswald text-[10px] font-bold uppercase tracking-widest text-primary bg-slate-950/95 border border-white/10 px-3.5 py-1.5 rounded-full absolute right-14 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 group-hover:-translate-x-1.5 pointer-events-none transition-all duration-300 shadow-2xl backdrop-blur-md whitespace-nowrap">
        Back to top
      </span>
    </button>
  )
}

